import React from "react";
import EngineeringIcon from "./EngineeringIcons";

const EngineeringEcosystem: React.FC = () => {
  const ecosystemData = [
    {
      category: "Foundation & Innovation",
      keystones: [
        {
          name: "Research & Development",
          description: "Pioneering breakthrough engineering solutions",
          icon: "🔬",
          impact: "Drives innovation across all sectors"
        },
        {
          name: "Software & Machine Learning",
          description: "Digital transformation and intelligent systems",
          icon: "💻",
          impact: "Automates and optimizes processes"
        }
      ]
    },
    {
      category: "Agricultural & Food Security",
      keystones: [
        {
          name: "Farm Machinery & Food Processing",
          description: "Advanced agricultural and food production systems",
          icon: "🚜",
          impact: "Ensures food security and agricultural efficiency"
        }
      ]
    },
    {
      category: "Energy & Sustainability",
      keystones: [
        {
          name: "Energy Engineering",
          description: "Renewable energy and sustainable power solutions",
          icon: "⚡",
          impact: "Powers sustainable development"
        },
        {
          name: "Sustainable Building",
          description: "Eco-friendly construction and materials",
          icon: "🏗️",
          impact: "Creates green infrastructure"
        }
      ]
    },
    {
      category: "Manufacturing & Infrastructure",
      keystones: [
        {
          name: "Wood Works Engineering",
          description: "Advanced wood processing and manufacturing",
          icon: "🪵",
          impact: "Efficient resource utilization"
        },
        {
          name: "Plumbing Engineering",
          description: "Comprehensive infrastructure systems",
          icon: "🔧",
          impact: "Builds essential infrastructure"
        }
      ]
    },
    {
      category: "Marine & Transportation",
      keystones: [
        {
          name: "Boats & Yachts Building",
          description: "Marine engineering and vessel construction",
          icon: "🚢",
          impact: "Connects communities and enables commerce"
        }
      ]
    },
    {
      category: "Human Capital Development",
      keystones: [
        {
          name: "TVET/Skills Training",
          description: "Technical and vocational education training",
          icon: "🎓",
          impact: "Empowers workforce development"
        }
      ]
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">The Ekumfi Engineering Ecosystem</h2>
          <p className="text-gray-600 max-w-4xl mx-auto text-lg">
            Our nine engineering keystones work in harmony to create a comprehensive industrial ecosystem 
            that drives sustainable development and empowers the community of Ekumfiman.
          </p>
        </div>

        {/* Ecosystem Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {ecosystemData.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow engineering-card">
              <h3 className="text-xl font-bold mb-4 text-orange-600">{category.category}</h3>
              <div className="space-y-4">
                {category.keystones.map((keystone, keystoneIndex) => (
                  <div key={keystoneIndex} className="border-l-4 border-orange-500 pl-4">
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl animate-pulley-bounce">{keystone.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 mb-1">{keystone.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{keystone.description}</p>
                        <div className="bg-orange-50 text-orange-700 px-2 py-1 rounded text-xs font-medium">
                          {keystone.impact}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Integration Section */}
        <div className="mt-16 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-8 rounded-lg">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">How It All Works Together</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl mb-2 animate-spin-slow">🔄</div>
                <h4 className="font-semibold mb-2">Integrated Approach</h4>
                <p className="text-orange-100 text-sm">
                  All keystones are interconnected, creating synergies that amplify their individual impact.
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-2 animate-pulley-bounce">🌱</div>
                <h4 className="font-semibold mb-2">Sustainable Growth</h4>
                <p className="text-orange-100 text-sm">
                  Each component contributes to long-term environmental and economic sustainability.
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-2 animate-bounce-slow">👥</div>
                <h4 className="font-semibold mb-2">Community Focus</h4>
                <p className="text-orange-100 text-sm">
                  Every initiative is designed to benefit and empower the local community.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Impact Metrics */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-500 mb-2">9</div>
            <div className="text-gray-600">Engineering Keystones</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-500 mb-2">∞</div>
            <div className="text-gray-600">Innovation Potential</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-500 mb-2">100%</div>
            <div className="text-gray-600">Sustainable Focus</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-500 mb-2">🌍</div>
            <div className="text-gray-600">Global Impact</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EngineeringEcosystem; 